import { DividerWithText, Paper, useDataQuery } from "@lyfegen-platform/react-common";
import { Box } from "@material-ui/core";
import { differenceInCalendarDays, format, isToday, isYesterday } from "date-fns";
import { createRef, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import SubscriptionForUpdates from "../../../hooks/subscription-for-updates";
import { SupportedMessageNodes } from "../../../models/generated/graphql";
import useUrlQuery from "../../../utils/use-url-query";
import CaseMessage from "./CaseMessage";
import CaseMessagesPlaceholder from "./CaseMessagesPlaceholder";
import { GET_MESSAGES, THREAD_SUBSCRIPTION } from "./queries";
import useMessagesStyles from "./use-messages-styles";
import { GetMessagesQuery } from "./types/queries.generated";
import LoadingBox from "../../../components/LoadingBox";

export type SingleMessage = NonNullable<NonNullable<GetMessagesQuery["GetMessages"]>[number]>;
interface DividerProps {
	previousMessage?: SingleMessage | null;
	message: SingleMessage;
}

const Divider: React.FC<DividerProps> = ({ previousMessage, message }) => {
	const { t } = useTranslation();
	const previousDate = new Date(previousMessage?.createdOn || 0);
	const date = new Date(message.createdOn || 0);

	const getDividerText = (date: Date): string => {
		if (isToday(date)) return t("today");

		if (isYesterday(date)) return t("yesterday");

		return format(date, "EEEE, LLLL dd");
	};

	if (previousMessage && !differenceInCalendarDays(previousDate, date)) return null;

	return (
		<Box paddingLeft={3} marginTop={2}>
			<DividerWithText text={getDividerText(date)} />
		</Box>
	);
};

interface MessagesProps {
	nodeId: string;
	nodeLabel: SupportedMessageNodes;
	deleteMessageCondition: boolean;
	isSettled: boolean;
}

const CaseMessages: React.FC<MessagesProps> = ({ nodeId, nodeLabel, deleteMessageCondition, isSettled }) => {
	const { messagesPaper, messagesContainer } = useMessagesStyles();
	const url = useUrlQuery();
	const messageLink = url.get("message");

	const { data, loading, refetch } = useDataQuery<GetMessagesQuery>(GET_MESSAGES, {
		variables: { id: nodeId, nodeLabel },
	});
	const messages = data?.GetMessages;

	const bottomRef = useRef(null);
	const refs =
		messageLink &&
		messages?.reduce((acc, value) => {
			const createdOn = "" + (value?.createdOn || 0);
			(acc as any)[createdOn] = createRef();
			return acc;
		}, {});

	const scrollToBottom = () => bottomRef.current && (bottomRef.current as any).scrollIntoView();
	const scrollToMessage = () => refs && messageLink && (refs as any)[messageLink]?.current.scrollIntoView();

	useEffect(() => {
		!messageLink ? scrollToBottom() : scrollToMessage();
	}, [data]);

	const onSubscriptionCompleted = () => {
		refetch();
		scrollToBottom();
	};

	if (loading) return <LoadingBox />;

	if (!messages?.length) return <CaseMessagesPlaceholder isSettled={isSettled} handleRefetch={refetch} scrollToBottom={scrollToBottom} />;

	return (
		<Box className={messagesContainer}>
			<Paper classes={{ root: messagesPaper }}>
				{messages.map(
					(message, i) =>
						message && (
							<div key={i} ref={refs && (refs as any)[message?.createdOn as unknown as string]}>
								<Divider previousMessage={messages[i - 1]} message={message} />
								<CaseMessage
									message={message}
									nodeId={nodeId}
									nodeLabel={nodeLabel}
									deleteMessageCondition={deleteMessageCondition}
								/>
							</div>
						)
				)}
			</Paper>
			<span ref={bottomRef}></span>
			<SubscriptionForUpdates onCompleted={onSubscriptionCompleted} query={THREAD_SUBSCRIPTION} />
		</Box>
	);
};

export default CaseMessages;
